/**
 * Automations (workflows in code and the API, at `/automations`): when
 * something happens to a record, if conditions hold, do steps. The one
 * place a workflow is edited; pages elsewhere only link here ("Used by").
 * Every automatic email is one: the email automations once made under
 * Emails were converted to workflows (server migration v55).
 *
 * The list is kept tidy: grouped by folder, with an owner, a health
 * column (running fine, erroring, waiting, never runs, off) and filters
 * for each, archived ones hidden unless asked for, an amber note where
 * two can email the same person on the same day, and a nudge when some
 * have not run in 30 days.
 *
 * Anyone but a scoped login reads this page; `can_manage`
 * (manage_workflows) switches, files, archives, copies and deletes. A
 * workflow that has run is archived, not deleted. `?new=1` (from the
 * command bar) opens the template picker; the filters live in the address
 * (`?health=never_runs&owner=me`, as the monthly nudge links).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Group, Loader, Menu, Select, Stack, Switch, Table,
  Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArchive, IconArrowBackUp, IconArrowRight, IconCopy, IconDots, IconFolder,
  IconFolderCog, IconPlus, IconSearch, IconTag, IconTrash,
} from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import {
  useWorkflowEdits, useWorkflows, type TriggerKind, type WorkflowSummary,
} from '../../api/workflows';
import { TOKENS, formatNumber } from '../../theme';
import { PageHeader } from '../../ui';
import { relativeTime } from '../../ui/format';
import { useVocab, type Vocab } from '../../vocab';
import { exactTime } from '../crm/Timeline';
import { HEALTH_LABEL, HEALTH_ORDER, HealthCell } from './health';
import {
  activeFilterCount, allTags, filterWorkflows, filtersFrom, filtersInto, groupByFolder, quietCount, type ListFilters,
} from './listModel';
import { FoldersModal, OrganiseModal } from './OrganiseModals';
import { TemplatePicker } from './TemplatePicker';
import { sendOnceStatus } from './sendOnce';
import { triggerLabel } from './workflowModel';

const once = (w: WorkflowSummary) => sendOnceStatus(w.send_once);

/** The organising principle, in one line. */
export function principle(v: Vocab): string {
  return `Routing decides who buys ${v.a('lead')} and at what price. ${v.Many('workflow')} decide what happens around the ${v.one('lead')} or ${v.one('person')} afterwards.`;
}

export function WorkflowsPage() {
  const v = useVocab();
  const list = useWorkflows();
  const me = useAuth().user?.id ?? null;
  const [params, setParams] = useSearchParams();
  const [picking, setPicking] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [filing, setFiling] = useState<WorkflowSummary | null>(null);
  const canManage = list.data?.can_manage ?? false;
  const filters = useMemo(() => filtersFrom(params), [params]);
  const grouped = params.get('group') !== '0';
  const setFilters = (patch: Partial<ListFilters>) =>
    setParams(filtersInto(params, { ...filters, ...patch }), { replace: true });

  // ?new=1 from the command bar opens the picker, once the list says this
  // person may make one; ?starter=<key> opens it at that starter. An email
  // to a list is a bulk send now, made on the Emails page: old links to that
  // starter go there.
  const navigate = useNavigate();
  const [starter, setStarter] = useState<string | null>(null);
  useEffect(() => {
    if (params.get('starter') === 'email_list_once') {
      navigate('/emails/bulk/new', { replace: true });
      return;
    }
    const wanted = params.get('starter');
    if ((params.get('new') === '1' || wanted) && canManage) {
      setStarter(wanted);
      setPicking(true);
      const next = new URLSearchParams(params);
      next.delete('new');
      next.delete('starter');
      setParams(next, { replace: true });
    }
  }, [params, setParams, canManage, navigate]);

  // Bulk sends are automations underneath, listed on the Emails page.
  const all = (list.data?.workflows ?? []).filter((w) => !w.bulk_send);
  const folders = list.data?.folders ?? [];
  const suggestions = list.data?.folder_suggestions ?? [];
  const shown = filterWorkflows(all, filters, me);
  const quiet = quietCount(all);
  const tags = allTags(all);
  const archivedCount = all.filter((w) => w.archived).length;

  return (
    <Stack gap="lg">
      <PageHeader
        title={v.Many('workflow')}
        description={<span data-testid="workflows-principle">{principle(v)}</span>}
        actions={canManage ? (
          <Group gap="sm">
            <Button variant="default" leftSection={<IconFolderCog size={16} />} onClick={() => setFoldersOpen(true)} data-testid="manage-folders">
              Folders
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setPicking(true)}>{`New ${v.one('workflow')}`}</Button>
          </Group>
        ) : undefined}
      />
      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}
      {list.isPending && <Group justify="center" p="xl"><Loader /></Group>}

      {quiet > 0 && filters.health !== 'never_runs' && (
        <Alert color="chartAmber" variant="light" p="xs" data-testid="tidy-banner" icon={<IconAlertTriangle size={16} />}>
          <Group justify="space-between" gap="sm" wrap="wrap">
            <Text size="sm">
              {`${v.count(quiet, 'workflow')} ${quiet === 1 ? "hasn't" : "haven't"} run in 30 days.`}
            </Text>
            <Button size="compact-sm" variant="light" color="chartAmber" onClick={() => setFilters({ health: 'never_runs' })} data-testid="tidy-review">
              Review
            </Button>
          </Group>
        </Alert>
      )}

      {list.data && all.length > 0 && (
        <Filters
          filters={filters}
          setFilters={setFilters}
          workflows={all}
          folders={folders.map((f) => f.name)}
          tags={tags}
          me={me}
          grouped={grouped}
          setGrouped={(g) => {
            const next = filtersInto(params, filters);
            if (g) next.delete('group');
            else next.set('group', '0');
            setParams(next, { replace: true });
          }}
          archivedCount={archivedCount}
          shown={shown.length}
        />
      )}

      {list.data && (
        all.length === 0 ? (
          <Card padding="lg">
            <Stack gap="xs" align="center">
              <Text size="sm" c="dimmed" ta="center">
                {`No ${v.many('workflow')} yet.${canManage ? ' Start from a template: assign each new one and give the rep a call to make.' : ''}`}
              </Text>
              {canManage && (
                <Button variant="light" size="xs" onClick={() => setPicking(true)}>{`New ${v.one('workflow')}`}</Button>
              )}
            </Stack>
          </Card>
        ) : shown.length === 0 ? (
          <Card padding="lg">
            <Stack gap="xs" align="center">
              <Text size="sm" c="dimmed" ta="center" data-testid="no-matches">{`No ${v.many('workflow')} match these filters.`}</Text>
              <Button variant="light" size="xs" onClick={() => setParams(filtersInto(params, { ...filters, ...CLEAR }), { replace: true })}>
                Clear filters
              </Button>
            </Stack>
          </Card>
        ) : (
          <WorkflowTable
            workflows={shown}
            canManage={canManage}
            grouped={grouped}
            folders={folders.map((f) => f.name)}
            onFile={setFiling}
          />
        )
      )}

      <TemplatePicker
        opened={picking}
        onClose={() => { setPicking(false); setStarter(null); }}
        templates={(list.data?.templates ?? []).filter((t) => t.key !== 'email_list_once')}
        starter={starter}
      />
      {foldersOpen && (
        <FoldersModal opened onClose={() => setFoldersOpen(false)} folders={folders} suggestions={suggestions} />
      )}
      {filing && (
        <OrganiseModal workflow={filing} folders={folders} suggestions={suggestions} tags={tags} onClose={() => setFiling(null)} />
      )}
    </Stack>
  );
}

const CLEAR: Partial<ListFilters> = { q: '', folder: '', owner: '', health: '', trigger: '', enabled: '', tag: '' };

function Filters({
  filters, setFilters, workflows, folders, tags, me, grouped, setGrouped, archivedCount, shown,
}: {
  filters: ListFilters;
  setFilters: (p: Partial<ListFilters>) => void;
  workflows: WorkflowSummary[];
  folders: string[];
  tags: string[];
  me: string | null;
  grouped: boolean;
  setGrouped: (g: boolean) => void;
  archivedCount: number;
  shown: number;
}) {
  const v = useVocab();
  const owners = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workflows) if (w.owner_user_id) m.set(w.owner_user_id, w.owner_name ?? w.owner_user_id);
    return [...m.entries()].filter(([id]) => id !== me).sort((a, b) => a[1].localeCompare(b[1]));
  }, [workflows, me]);
  const triggers = useMemo(() => [...new Set(workflows.map((w) => w.trigger.kind))], [workflows]);
  const count = activeFilterCount(filters);
  const sel = { size: 'xs' as const, clearable: true, comboboxProps: { withinPortal: true }, w: 160 };
  return (
    <Stack gap={8} data-testid="workflow-filters">
      <Group gap="xs" wrap="wrap" align="flex-end">
        <TextInput
          size="xs"
          placeholder="Search by name"
          leftSection={<IconSearch size={14} />}
          value={filters.q}
          onChange={(e) => setFilters({ q: e.currentTarget.value })}
          w={200}
          aria-label="Search by name"
          data-testid="filter-q"
        />
        <Select
          {...sel}
          placeholder="Any folder"
          aria-label="Folder"
          data={[{ value: 'none', label: 'No folder' }, ...folders.map((f) => ({ value: f, label: f }))]}
          value={filters.folder || null}
          onChange={(x) => setFilters({ folder: x ?? '' })}
          data-testid="filter-folder"
        />
        <Select
          {...sel}
          placeholder="Anyone's"
          aria-label={v.One('owner')}
          data={[
            { value: 'me', label: `My ${v.many('workflow')}` },
            ...owners.map(([id, name]) => ({ value: id, label: name })),
            { value: 'none', label: `No ${v.one('owner')}` },
          ]}
          value={filters.owner || null}
          onChange={(x) => setFilters({ owner: x ?? '' })}
          data-testid="filter-owner"
        />
        <Select
          {...sel}
          placeholder="Any health"
          aria-label="Health"
          data={HEALTH_ORDER.filter((h) => h !== 'archived').map((h) => ({ value: h, label: HEALTH_LABEL[h] }))}
          value={filters.health || null}
          onChange={(x) => setFilters({ health: (x ?? '') as ListFilters['health'] })}
          data-testid="filter-health"
        />
        <Select
          {...sel}
          w={220}
          placeholder="Any trigger"
          aria-label="Trigger"
          data={triggers.map((k) => ({ value: k, label: triggerLabel(v, k as TriggerKind) }))}
          value={filters.trigger || null}
          onChange={(x) => setFilters({ trigger: (x ?? '') as ListFilters['trigger'] })}
          data-testid="filter-trigger"
        />
        <Select
          {...sel}
          w={120}
          placeholder="On or off"
          aria-label="On or off"
          data={[{ value: 'on', label: 'On' }, { value: 'off', label: 'Off' }]}
          value={filters.enabled || null}
          onChange={(x) => setFilters({ enabled: (x ?? '') as ListFilters['enabled'] })}
          data-testid="filter-enabled"
        />
        {tags.length > 0 && (
          <Select
            {...sel}
            w={140}
            placeholder="Any tag"
            aria-label="Tag"
            leftSection={<IconTag size={13} />}
            data={tags}
            value={filters.tag || null}
            onChange={(x) => setFilters({ tag: x ?? '' })}
            data-testid="filter-tag"
          />
        )}
      </Group>
      <Group gap="md" wrap="wrap">
        <Switch size="xs" label="Group by folder" checked={grouped} onChange={(e) => setGrouped(e.currentTarget.checked)} data-testid="group-by-folder" />
        <Switch
          size="xs"
          label={`Show archived${archivedCount ? ` (${archivedCount})` : ''}`}
          checked={filters.archived}
          onChange={(e) => setFilters({ archived: e.currentTarget.checked })}
          data-testid="show-archived"
        />
        <Text size="xs" c="dimmed">{`${v.count(shown, 'workflow')} shown`}</Text>
        {count > 0 && (
          <Anchor size="xs" component="button" type="button" onClick={() => setFilters({ ...CLEAR, archived: false })}>
            Clear filters
          </Anchor>
        )}
      </Group>
    </Stack>
  );
}

/** "Can email the same person on the same day as 'Welcome' and 2 more". */
export function overlapNote(w: WorkflowSummary): string | null {
  if (!w.overlaps.length) return null;
  const [first, ...rest] = w.overlaps;
  return `Can email the same person on the same day as '${first!.name}'${rest.length ? ` and ${rest.length} more` : ''}`;
}

function WorkflowTable({
  workflows, canManage, grouped, folders, onFile,
}: {
  workflows: WorkflowSummary[];
  canManage: boolean;
  grouped: boolean;
  folders: string[];
  onFile: (w: WorkflowSummary) => void;
}) {
  const v = useVocab();
  const navigate = useNavigate();
  const edits = useWorkflowEdits();
  const [busy, setBusy] = useState<string | null>(null);
  const groups = grouped ? groupByFolder(workflows, folders) : [{ folder: null as string | null, workflows }];

  async function toggle(w: WorkflowSummary, enabled: boolean) {
    setBusy(w.id);
    try {
      await edits.setEnabled.mutateAsync({ id: w.id, enabled });
      notifications.show({ color: 'teal', title: enabled ? 'Switched on' : 'Switched off', message: w.name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function duplicate(w: WorkflowSummary) {
    try {
      const copy = await edits.duplicate.mutateAsync(w.id);
      notifications.show({ color: 'teal', title: 'Copied', message: `${copy.name}. The copy is off.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not copied', message: (e as Error).message });
    }
  }

  async function archive(w: WorkflowSummary) {
    const going = w.in_progress > 0 ? ` ${v.count(w.in_progress, 'run')} still going will be cancelled.` : '';
    if (!window.confirm(`Archive ${w.name}? It is switched off and hidden from the list.${going} Its history is kept, and it can be restored.`)) return;
    try {
      await edits.archive.mutateAsync(w.id);
      notifications.show({ color: 'teal', title: 'Archived', message: w.name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not archived', message: (e as Error).message });
    }
  }

  async function restore(w: WorkflowSummary) {
    try {
      await edits.restore.mutateAsync(w.id);
      notifications.show({ color: 'teal', title: 'Restored', message: `${w.name}. It is off until you switch it on.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not restored', message: (e as Error).message });
    }
  }

  async function del(w: WorkflowSummary) {
    if (!window.confirm(`Delete ${w.name}? It has never run, so nothing else goes with it.`)) return;
    try {
      await edits.remove.mutateAsync(w.id);
      notifications.show({ color: 'teal', title: 'Deleted', message: w.name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message });
    }
  }

  return (
    <Card padding={0}>
      <Table.ScrollContainer minWidth={980}>
        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Health</Table.Th>
              <Table.Th>{v.One('owner')}</Table.Th>
              <Table.Th w={70}>On</Table.Th>
              <Table.Th>{`${v.Many('run')}, 7 days`}</Table.Th>
              <Table.Th>Last run</Table.Th>
              <Table.Th w={44} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map((g) => (
              <Fragment key={g.folder ?? 'all'}>
                {g.folder !== null && (
                  <Table.Tr data-testid={`folder-group-${g.folder || 'none'}`} style={{ background: TOKENS.surface2 }}>
                    <Table.Td colSpan={7} py={6}>
                      <Group gap={6} wrap="nowrap">
                        <IconFolder size={14} color={TOKENS.textSecondary} />
                        <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: '0.04em', color: TOKENS.textSecondary }}>
                          {g.folder || 'No folder'}
                        </Text>
                        <Text size="xs" c="dimmed">{g.workflows.length}</Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
                {g.workflows.map((w) => {
                  const note = overlapNote(w);
                  return (
                    <Table.Tr key={w.id} data-testid={`workflow-row-${w.id}`} style={w.archived ? { opacity: 0.7 } : undefined}>
                      <Table.Td style={{ minWidth: 260, maxWidth: 400 }}>
                        <Group gap={6} wrap="nowrap">
                          <Anchor component={Link} to={`/automations/${encodeURIComponent(w.id)}`} fw={600} size="sm" style={{ color: TOKENS.text }}>
                            {w.name}
                          </Anchor>
                          {w.archived && <Badge size="xs" variant="outline" color="gray" radius="sm" tt="none">Archived</Badge>}
                        </Group>
                        <Text size="xs" style={{ color: TOKENS.textSecondary }} lineClamp={2}>{w.trigger_text}</Text>
                        {once(w) && (
                          <Badge size="sm" mt={4} variant="light" color={once(w)!.color} radius="sm" tt="none" data-testid="send-once-status">
                            {once(w)!.text}
                          </Badge>
                        )}
                        <Group gap={4} mt={2} wrap="wrap">
                          <Text size="xs" c="dimmed">{`${formatNumber(w.step_count)} ${w.step_count === 1 ? 'step' : 'steps'} · version ${w.version}`}</Text>
                          {!grouped && w.folder && (
                            <Badge size="xs" variant="light" color="gray" radius="sm" tt="none" leftSection={<IconFolder size={10} />}>{w.folder}</Badge>
                          )}
                          {w.tags.map((t) => (
                            <Badge key={t} size="xs" variant="outline" color="gray" radius="sm" tt="none">{t}</Badge>
                          ))}
                        </Group>
                        {note && (
                          <Text size="xs" mt={4} style={{ color: TOKENS.amberText, display: 'flex', gap: 4, alignItems: 'flex-start' }} data-testid="overlap-note">
                            <IconAlertTriangle size={13} style={{ flex: 'none', marginTop: 1 }} />
                            {note}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td style={{ maxWidth: 240 }}>
                        <HealthCell v={v} h={w.health} />
                        {w.health.state === 'erroring' && w.last_error && (
                          <Text size="xs" lineClamp={1} style={{ color: TOKENS.redText, overflowWrap: 'anywhere' }} data-testid="last-error">
                            {w.last_error}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={w.owner_name ? undefined : 'dimmed'} data-testid="owner">{w.owner_name ?? 'Nobody'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip
                          label={!canManage ? `Needs permission to manage ${v.many('workflow')}` : w.archived ? 'Restore it first' : w.enabled ? 'Switch off' : 'Switch on'}
                          openDelay={300}
                        >
                          <div style={{ display: 'inline-block' }}>
                            <Switch
                              checked={w.enabled}
                              disabled={!canManage || busy === w.id || w.archived}
                              onChange={(e) => toggle(w, e.currentTarget.checked)}
                              aria-label={`${w.name} on`}
                              data-testid={`toggle-${w.id}`}
                            />
                          </div>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(w.runs_7d)}</Text>
                        {w.failed_7d > 0 && (
                          <Text size="xs" style={{ color: TOKENS.redText }}>{`${formatNumber(w.failed_7d)} failed`}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {w.last_run_at ? (
                          <Tooltip label={exactTime(w.last_run_at)} openDelay={300}>
                            <Text size="sm" component="time" dateTime={w.last_run_at}>{relativeTime(w.last_run_at)}</Text>
                          </Tooltip>
                        ) : (
                          <Text size="sm" c="dimmed">Never</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" aria-label={`${w.name} actions`}>
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconArrowRight size={14} />} onClick={() => navigate(`/automations/${encodeURIComponent(w.id)}`)}>
                              Open
                            </Menu.Item>
                            {canManage && (
                              <>
                                <Menu.Item leftSection={<IconFolder size={14} />} onClick={() => onFile(w)}>{`Folder, tags and ${v.one('owner')}`}</Menu.Item>
                                <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => duplicate(w)}>Duplicate</Menu.Item>
                                {w.archived ? (
                                  <Menu.Item leftSection={<IconArrowBackUp size={14} />} onClick={() => restore(w)}>Restore</Menu.Item>
                                ) : (
                                  <Menu.Item leftSection={<IconArchive size={14} />} onClick={() => archive(w)}>Archive</Menu.Item>
                                )}
                                {w.total_runs > 0 ? (
                                  <Tooltip label="It has run, so archive it instead: its history is kept." position="left" openDelay={200}>
                                    <div>
                                      <Menu.Item color="red" leftSection={<IconTrash size={14} />} disabled>Delete</Menu.Item>
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => del(w)}>Delete</Menu.Item>
                                )}
                              </>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

